import { useState } from 'react';
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useTranslation } from 'react-i18next';
import i18n from 'i18next';
import { apiRequest } from '@/lib/queryClient';
import { useToast } from "@/hooks/use-toast";
import { LogoLoader } from '@/components/ui/logo-loader';
import { getSortedCountries } from '@/constants/countries';

interface CreateOrganizationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: (orgId: string) => void;
}

export function CreateOrganizationModal({ open, onOpenChange, onSuccess }: CreateOrganizationModalProps) {
  const { t } = useTranslation(['pages.settings']);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [formData, setFormData] = useState({
    name: '',
    website: '',
    industry: '',
    size: '',
    street: '',
    houseNumber: '',
    postalCode: '',
    city: '',
    country: '',
    contactEmail: '',
    contactPhone: '',
  });

  const createOrgMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      const res = await apiRequest('/api/orgs', {
        method: 'POST',
        body: JSON.stringify({
          name: data.name,
          website: data.website || undefined,
          industry: data.industry || undefined,
          size: data.size || undefined,
          street: data.street || undefined,
          houseNumber: data.houseNumber || undefined,
          postalCode: data.postalCode || undefined,
          city: data.city || undefined,
          country: data.country || undefined,
          contact_email: data.contactEmail || undefined,
          contact_phone: data.contactPhone || undefined,
        }),
      });
      return await res.json();
    },
    onSuccess: (response) => {
      const orgId = (response as any)?.data?.id;
      if (orgId) {
        toast({
          title: t('pages.settings:organization.createSuccess'),
          description: t('pages.settings:organization.createSuccessDesc'),
        });
        onSuccess(orgId);
        // Reset form
        setFormData({
          name: '',
          website: '',
          industry: '',
          size: '',
          street: '',
          houseNumber: '',
          postalCode: '',
          city: '',
          country: '',
          contactEmail: '',
          contactPhone: '',
        });
      }
    },
    onError: (error: any) => {
      toast({
        title: t('pages.settings:organization.createError'),
        description: error?.message || t('pages.settings:organization.createErrorDesc'),
        variant: 'destructive',
      });
    },
  });

  const handleSubmit = () => {
    if (!formData.name.trim()) {
      toast({
        title: t('pages.settings:organization.createError'),
        description: t('pages.settings:organization.nameRequired'),
        variant: 'destructive',
      });
      return;
    }
    createOrgMutation.mutate(formData);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('pages.settings:organization.createModal.title')}</DialogTitle>
          <DialogDescription>
            {t('pages.settings:organization.createModal.description')}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2 md:col-span-2">
              <Label className="text-xs">{t('pages.settings:organization.profile.name')} *</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="h-8 text-xs"
                placeholder={t('pages.settings:organization.profile.namePlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{t('pages.settings:organization.profile.website')}</Label>
              <Input
                value={formData.website}
                onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                className="h-8 text-xs"
                placeholder={t('pages.settings:organization.profile.websitePlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{t('pages.settings:organization.profile.industry')}</Label>
              <Input
                value={formData.industry}
                onChange={(e) => setFormData({ ...formData, industry: e.target.value })}
                className="h-8 text-xs"
                placeholder={t('pages.settings:organization.profile.industryPlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{t('pages.settings:organization.profile.size')}</Label>
              <Select
                value={formData.size}
                onValueChange={(value) => setFormData({ ...formData, size: value })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder={t('pages.settings:organization.profile.sizePlaceholder')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="small">{t('pages.settings:organization.profile.sizeOptions.small')}</SelectItem>
                  <SelectItem value="medium">{t('pages.settings:organization.profile.sizeOptions.medium')}</SelectItem>
                  <SelectItem value="large">{t('pages.settings:organization.profile.sizeOptions.large')}</SelectItem>
                  <SelectItem value="enterprise">{t('pages.settings:organization.profile.sizeOptions.enterprise')}</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{t('pages.settings:sections.profile.fields.street')}</Label>
              <Input
                value={formData.street}
                onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                className="h-8 text-xs"
                placeholder={t('pages.settings:sections.profile.fields.streetPlaceholder') as string}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{t('pages.settings:sections.profile.fields.houseNumber')}</Label>
              <Input
                value={formData.houseNumber}
                onChange={(e) => setFormData({ ...formData, houseNumber: e.target.value })}
                className="h-8 text-xs"
                placeholder={t('pages.settings:sections.profile.fields.houseNumberPlaceholder') as string}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{t('pages.settings:sections.profile.fields.postalCode')}</Label>
              <Input
                value={formData.postalCode}
                onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                className="h-8 text-xs"
                placeholder={t('pages.settings:sections.profile.fields.postalCodePlaceholder') as string}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{t('pages.settings:sections.profile.fields.city')}</Label>
              <Input
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="h-8 text-xs"
                placeholder={t('pages.settings:sections.profile.fields.cityPlaceholder') as string}
              />
            </div>
            <div className="space-y-2 md:col-span-2">
              <Label className="text-xs">{t('pages.settings:sections.profile.fields.country')}</Label>
              <Select
                value={formData.country}
                onValueChange={(value) => setFormData({ ...formData, country: value })}
              >
                <SelectTrigger className="h-8 text-xs">
                  <SelectValue placeholder={t('pages.settings:sections.profile.fields.countryPlaceholder') as string} />
                </SelectTrigger>
                <SelectContent>
                  {getSortedCountries(i18n.language as 'en' | 'de' | 'tr').map((country) => (
                    <SelectItem key={country.code} value={country.code} className="text-xs">
                      {country[i18n.language as 'en' | 'de' | 'tr']}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{t('pages.settings:organization.profile.contactEmail')}</Label>
              <Input
                type="email"
                value={formData.contactEmail}
                onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                className="h-8 text-xs"
                placeholder={t('pages.settings:organization.profile.contactEmailPlaceholder')}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs">{t('pages.settings:organization.profile.contactPhone')}</Label>
              <Input
                value={formData.contactPhone}
                onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                className="h-8 text-xs"
                placeholder={t('pages.settings:organization.profile.contactPhonePlaceholder')}
              />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={createOrgMutation.isPending}
            size="sm"
            className="h-8 text-xs"
          >
            {t('pages.settings:organization.createModal.cancel')}
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={createOrgMutation.isPending || !formData.name.trim()}
            size="sm"
            className="h-8 text-xs"
          >
            {createOrgMutation.isPending ? (
              <>
                <LogoLoader size="sm" />
                {t('pages.settings:organization.createModal.creating')}
              </>
            ) : (
              t('pages.settings:organization.createModal.create')
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

